import { useState, useEffect, useRef } from 'react'
import {
  FiBell,
  FiX,
  FiCheck,
  FiCheckCircle,
  FiLoader,
  FiTrash2,
} from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [showDropdown, setShowDropdown] = useState(false)
  const [loading, setLoading] = useState(false)
  const [markingAsRead, setMarkingAsRead] = useState(null)
  const [clearingAll, setClearingAll] = useState(false)

  const navigate = useNavigate()
  const dropdownRef = useRef(null)

  useEffect(() => {
    fetchNotifications()

    const interval = setInterval(fetchNotifications, 30000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false)
      }
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleOutsideClick)
      document.addEventListener('touchstart', handleOutsideClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('touchstart', handleOutsideClick)
    }
  }, [showDropdown])

  useEffect(() => {
    if (showDropdown && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }

    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [showDropdown])

  const fetchNotifications = async () => {
    try {
      setLoading(true)

      const response = await axios.get('/notifications', {
        params: {
          page: 1,
          limit: 8,
        },
      })

      setNotifications(response.data.notifications || [])
      setUnreadCount(response.data.unreadCount || 0)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (e, notificationId) => {
    e.stopPropagation()

    setMarkingAsRead(notificationId)

    try {
      await axios.patch(`/notifications/${notificationId}/read`)

      setNotifications((prev) =>
        prev.map((notification) =>
          notification._id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      )

      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Error marking notification as read:', error)
      toast.error('Failed to mark notification as read')
    } finally {
      setMarkingAsRead(null)
    }
  }

  const markAllAsRead = async () => {
    try {
      await axios.patch('/notifications/mark-all-read')

      setNotifications((prev) =>
        prev.map((notification) => ({
          ...notification,
          read: true,
        }))
      )

      setUnreadCount(0)

      toast.success('All notifications marked as read')
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
      toast.error('Failed to mark all notifications as read')
    }
  }

  const clearAllNotifications = async () => {
    if (!window.confirm('Are you sure you want to clear all notifications? This action cannot be undone.')) {
      return
    }

    setClearingAll(true)

    try {
      await axios.delete('/notifications')

      setNotifications([])
      setUnreadCount(0)

      toast.success('All notifications cleared')
    } catch (error) {
      console.error('Error clearing all notifications:', error)
      toast.error('Failed to clear all notifications')
    } finally {
      setClearingAll(false)
    }
  }

  const deleteNotification = async (e, notificationId) => {
    e.stopPropagation()

    try {
      await axios.delete(`/notifications/${notificationId}`)

      setNotifications((prev) =>
        prev.filter(
          (notification) => notification._id !== notificationId
        )
      )

      toast.success('Notification deleted')
    } catch (error) {
      console.error('Error deleting notification:', error)
      toast.error('Failed to delete notification')
    }
  }

  const handleViewAll = () => {
    setShowDropdown(false)
    navigate('/notifications')
  }

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return (
          <FiCheckCircle className="w-4 h-4 text-green-600" />
        )

      case 'error':
        return <FiX className="w-4 h-4 text-red-600" />

      case 'warning':
        return <FiBell className="w-4 h-4 text-yellow-600" />

      default:
        return <FiBell className="w-4 h-4 text-blue-600" />
    }
  }

  const formatTime = (date) => {
    const now = new Date()
    const diff = now - new Date(date)

    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`

    return new Date(date).toLocaleDateString()
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Button */}
      <button
        onClick={() => setShowDropdown((prev) => !prev)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 touch-manipulation"
        title="Notifications"
        aria-label="Notifications"
      >
        <FiBell className="w-5 h-5 text-gray-700" />

        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-semibold min-w-[20px] h-5 px-1 rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Modal */}
      {showDropdown && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px]"
            onClick={() => setShowDropdown(false)}
          />

          {/* Panel */}
          <div
            className="
              fixed z-50 bg-white border border-gray-200 shadow-2xl
              flex flex-col overflow-hidden

              top-16 left-1/2 -translate-x-1/2
              w-[calc(100vw-1rem)]
              max-w-[95vw]
              max-h-[calc(100vh-5rem)]
              rounded-2xl

              sm:top-20
              sm:w-[28rem]
              sm:max-w-[90vw]
              sm:max-h-[85vh]

              lg:absolute
              lg:top-full
              lg:right-0
              lg:left-auto
              lg:translate-x-0
              lg:mt-3
              lg:w-[28rem]
              lg:max-w-[28rem]
              lg:max-h-[32rem]
            "
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-4 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 text-base truncate">
                  Notifications
                </h3>

                {unreadCount > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {unreadCount} unread
                  </p>
                )}
              </div>

              <button
                onClick={() => setShowDropdown(false)}
                className="p-2 rounded-lg hover:bg-gray-200 transition-colors flex-shrink-0 touch-manipulation"
              >
                <FiX className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain divide-y divide-gray-100">
              {loading ? (
                <div className="p-8 flex items-center justify-center">
                  <FiLoader className="w-5 h-5 text-gray-400 animate-spin" />
                </div>
              ) : notifications.length > 0 ? (
                notifications.map((notification) => (
                  <div
                    key={notification._id}
                    className={`p-4 hover:bg-gray-50 transition-colors duration-150 ${
                      !notification.read ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 break-words leading-5">
                              {notification.title}
                            </p>

                            <p className="text-sm text-gray-600 mt-1 break-words leading-5">
                              {notification.message}
                            </p>

                            <p className="text-xs text-gray-500 mt-2">
                              {formatTime(notification.createdAt)}
                            </p>
                          </div>

                          {!notification.read && (
                            <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2" />
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {!notification.read && (
                            <button
                              onClick={(e) =>
                                markAsRead(e, notification._id)
                              }
                              disabled={
                                markingAsRead === notification._id
                              }
                              className="min-h-[38px] px-3 py-2 text-xs rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors disabled:opacity-50 flex items-center gap-1 whitespace-nowrap touch-manipulation"
                            >
                              {markingAsRead ===
                              notification._id ? (
                                <FiLoader className="w-3 h-3 animate-spin" />
                              ) : (
                                <FiCheck className="w-3 h-3" />
                              )}

                              Mark as read
                            </button>
                          )}

                          <button
                            onClick={(e) =>
                              deleteNotification(
                                e,
                                notification._id
                              )
                            }
                            className="min-h-[38px] px-3 py-2 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors flex items-center justify-center touch-manipulation"
                          >
                            <FiTrash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center">
                  <FiBell className="w-8 h-8 text-gray-300 mx-auto mb-2" />

                  <p className="text-gray-500 text-sm">
                    No notifications available
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex flex-col gap-2 flex-shrink-0">
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={markAllAsRead}
                    disabled={unreadCount === 0}
                    className="flex-1 min-h-[44px] px-4 py-3 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 touch-manipulation"
                  >
                    <FiCheckCircle className="w-4 h-4" />
                    Mark all as read
                  </button>

                  <button
                    onClick={clearAllNotifications}
                    disabled={clearingAll}
                    className="flex-1 min-h-[44px] px-4 py-3 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:bg-red-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 touch-manipulation"
                  >
                    {clearingAll ? (
                      <FiLoader className="w-4 h-4 animate-spin" />
                    ) : (
                      <FiTrash2 className="w-4 h-4" />
                    )}
                    Clear all
                  </button>
                </div>

                <button
                  onClick={handleViewAll}
                  className="min-h-[44px] px-4 py-3 rounded-lg bg-gray-200 text-gray-900 text-sm font-medium hover:bg-gray-300 transition-colors touch-manipulation"
                >
                  View all
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}


